<script setup lang="ts">
import MultiSelectAudiences from '@/components/ui/MultiSelectAudiences.vue'

import type { Audience } from '@/types/types'
import type { TargetAudience } from '@/types/offer'
import type { RadioCardOption } from '@/components/ui/AppRadioCard.vue'

const props = defineProps<{
  allCustomerLists: Audience[]
}>()

const emit = defineEmits<{
  (e: 'hasError', value: boolean): void
}>()

const targetAudience = defineModel<TargetAudience>('targetAudience', { required: true })
const selectedSegmentIds = defineModel<number[]>('selectedSegmentIds', { required: true })

const { t } = useI18n()

const options = computed<(RadioCardOption & { id: TargetAudience })[]>(() => [
  {
    id: 'all',
    title: t('pages.offer.reward.targetAudienceAll'),
    description: t('pages.offer.reward.targetAudienceAllDesc')
  },
  {
    id: 'loyal',
    title: t('pages.offer.reward.targetAudienceLoyal'),
    description: t('pages.offer.reward.targetAudienceLoyalDesc')
  },
  {
    id: 'segment',
    title: t('pages.offer.reward.targetAudienceSegment'),
    description: t('pages.offer.reward.targetAudienceSegmentDesc')
  }
])

const selectedOption = computed({
  get: () => options.value.find((o) => o.id === targetAudience.value),
  set: (opt: RadioCardOption & { id: TargetAudience }) => {
    targetAudience.value = opt.id
    if (opt.id === 'all' || opt.id === 'loyal') {
      selectedSegmentIds.value = []
    }
  }
})

const hasSegmentError = computed(
  () => targetAudience.value === 'segment' && selectedSegmentIds.value.length === 0 && props.allCustomerLists.length > 0
)

watch(hasSegmentError, (val) => emit('hasError', val), { immediate: true })
</script>

<template>
  <Card>
    <CardHeader>
      <CardTitle>{{ t('pages.offer.reward.targetAudience') }}</CardTitle>
    </CardHeader>
    <CardContent>
      <AppRadioCard v-if="allCustomerLists.length > 0" v-model="selectedOption" :options="options">
        <template #2="{ checked }">
          <div class="flex min-w-0 flex-col gap-1">
            <span class="truncate text-sm font-medium text-foreground">
              {{ t('pages.offer.reward.targetAudienceSegment') }}
            </span>
            <span class="text-xs text-muted-foreground">
              {{ t('pages.offer.reward.targetAudienceSegmentDesc') }}
            </span>
            <div v-if="checked" class="mt-5" @click.stop>
              <MultiSelectAudiences v-model="selectedSegmentIds" :audiences="allCustomerLists" hide-empty-categories />
            </div>
            <p v-if="hasSegmentError" class="mt-1 text-xs text-red-500">
              {{ t('pages.offer.reward.segmentRequiredError') }}
            </p>
          </div>
        </template>
      </AppRadioCard>
    </CardContent>
  </Card>
</template>
